// TimerWidget — the shell-level running timer (Epic 4 · S4.2).
//
// Lives in the shell, on every hub page, in every workspace. The timer is the
// whole product; it is startable from a work card or a thread (pages call
// useTimer().start(attachment)) and nobody navigates to Effort to start timing.
//
// Two pieces, one component:
//   1. A floating chip (bottom-right) that appears whenever a timer is running and
//      the panel is closed — the "timer is on every page" signal. Shows elapsed,
//      title, Stop.
//   2. A panel (popover) opened by the top-bar timer button — full detail + Stop
//      when running, or a quick-start (a note) when nothing is running.
//
// fn_timer_start -> { id, started_at, stopped_previous, suggested }. One running
// timer per person (DB index); starting a second stops the first WITHOUT erroring
// (stopped_previous) — the context just adopts the new running timer. We surface
// `stopped_previous` as a brief note so the person knows their previous timer was
// closed. `needs_review` is true over 8h and is surfaced for correction in the
// stop dialog, never silently accepted.
//
// The stop dialog captures the description on STOP (pre-filled from `suggested`),
// never on start. We reuse the Effort page's StopTimerDialog verbatim — it is a
// presentational component with no effort-specific deps. The shell owns the timer
// (contexts/TimerContext) and the Effort page consumes the same one.

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTimer } from '../../../contexts/TimerContext';
import { useShellSurface } from '../../../contexts/ShellSurfaceContext';
import { ClientSlideOut } from '../kit';
import StopTimerDialog from '../effort/StopTimerDialog';

const two = (n) => String(n).padStart(2, '0');
const formatClock = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${two(m)}:${two(sec)}`;
};

export default function TimerWidget() {
  const { running, elapsedSeconds, start, stop } = useTimer();
  const { timer: panelOpen, openTimer, closeTimer } = useShellSurface();
  // The Effort page already shows the running timer in its always-visible "Active
  // timer" hero, so the floating chip would be a second copy of the same thing.
  // Suppress the chip there (only the chip — the top-bar timer panel still opens).
  const onEffortPage = useLocation().pathname.startsWith('/hub/effort');
  const [stopOpen, setStopOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [stoppedPrev, setStoppedPrev] = useState(false);

  const handleQuickStart = async () => {
    setError(null);
    try {
      const res = await start({ note: note.trim() || null });
      if (res?.stopped_previous) setStoppedPrev(true);
      setNote('');
      closeTimer();
    } catch (err) {
      setError(err?.message || "Couldn't start the timer.");
    }
  };

  const handleStop = async (description, reason) => {
    setSaving(true);
    setError(null);
    try {
      await stop(description, reason);
      setStopOpen(false);
      closeTimer();
    } catch (err) {
      setError(err?.message || 'Could not stop the timer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Floating chip — visible when a timer is running AND the panel is closed,
          EXCEPT on the Effort page whose hero already shows the running timer. The
          "timer is on every page" signal. */}
      {running && !panelOpen && !onEffortPage && (
        <button
          type="button"
          onClick={openTimer}
          className="fixed bottom-4 right-4 z-40 flex items-center gap-3 rounded-xl border border-white/40 bg-white/70 px-3 py-2 shadow-lg backdrop-blur-xl dark:border-white/10 dark:bg-gray-900/60"
          data-testid="timer-chip"
          aria-label={`Timer running: ${running.title || 'untitled work'}`}
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#32B1FF] opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#32B1FF]" />
          </span>
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {running.title || 'Untitled work'}
          </span>
          <span
            className="text-sm font-semibold tabular-nums text-gray-700 dark:text-gray-200"
            data-testid="timer-chip-elapsed"
            aria-live="polite"
          >
            {formatClock(elapsedSeconds)}
          </span>
          <span
            className="rounded-md bg-gray-900 px-2 py-1 text-xs font-semibold text-white dark:bg-white dark:text-gray-900"
            onClick={(e) => { e.stopPropagation(); setStopOpen(true); }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setStopOpen(true); } }}
            data-testid="timer-chip-stop"
          >
            Stop
          </span>
        </button>
      )}
      {/* Panel — opened from the top-bar timer button. */}
      {panelOpen && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Timer">
          <div className="absolute inset-0" onClick={closeTimer} aria-hidden="true" />
          <div
            className="absolute right-3 top-14 w-80 rounded-xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-800 dark:bg-gray-900"
            data-testid="timer-panel"
          >
            {running ? (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#32B1FF]">Running</p>
                <p className="mt-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {running.title || 'Untitled work'}
                </p>
                {running.client && (
                  <div className="mt-1">
                    <ClientSlideOut clientId={running.client_id} clientName={running.client} />
                  </div>
                )}
                <p
                  className="mt-2 text-3xl font-bold tabular-nums text-gray-900 dark:text-gray-100"
                  data-testid="timer-panel-elapsed"
                  aria-live="polite"
                >
                  {formatClock(elapsedSeconds)}
                </p>
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeTimer}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => setStopOpen(true)}
                    className="rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 dark:bg-white dark:text-gray-900"
                    data-testid="timer-panel-stop"
                  >
                    Stop
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Start a timer</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Quick start a generic timer here, or start one from a task or thread to attach it.
                </p>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder="What are you working on? (optional)"
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                  aria-label="Timer note"
                  data-testid="timer-quick-note"
                />
                {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeTimer}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleQuickStart}
                    className="rounded-lg bg-[#32B1FF] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#1a9ce6]"
                    data-testid="timer-quick-start"
                  >
                    Start
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* stopped_previous toast — surfaces OUTSIDE the panel so it survives the
          panel closing on a quick start. fn_timer_start stops the previous timer
          without erroring; the person needs to know their previous timer was closed. */}
      {stoppedPrev && (
        <div
          className="fixed bottom-20 right-4 z-40 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 shadow-lg dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
          data-testid="timer-stopped-previous"
          role="status"
        >
          Your previous timer was stopped automatically.
        </div>
      )}

      {stopOpen && running && (
        <StopTimerDialog
          suggested={running.suggested}
          overEight={running.over_8h}
          saving={saving}
          error={error}
          onSave={handleStop}
          onClose={() => setStopOpen(false)}
        />
      )}
    </>
  );
}
